import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Plus,
  RefreshCw,
  Search,
  Pencil,
  Trash2,
  GripVertical,
  ChevronDown,
  ChevronRight,
  X,
  AlertTriangle,
  CheckCircle2,
  Clock,
  FolderTree,
} from "lucide-react";
import API from "../../../util/API"; // your axios instance
// If you use react-toastify in project:
import { toast } from "react-toastify";

import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const ACCENT = "#D2042D";
const CATEGORY_TREE_MAX_DEPTH = 2; // root + 2 nested levels = 3 layers total
const cx = (...arr) => arr.filter(Boolean).join(" ");

const Card = ({ children, className }) => (
  <div
    className={cx("bg-white border border-slate-200 rounded-2xl", className)}
  >
    {children}
  </div>
);

const Skeleton = ({ className }) => (
  <div className={cx("animate-pulse rounded-xl bg-slate-100", className)} />
);

const Pill = ({ icon: Icon, tone = "neutral", children }) => {
  const tones = {
    good: "border-emerald-200 bg-emerald-50 text-emerald-700",
    warn: "border-amber-200 bg-amber-50 text-amber-700",
    bad: "border-rose-200 bg-rose-50 text-rose-700",
    neutral: "border-slate-200 bg-white text-slate-600",
  };
  return (
    <span
      className={cx(
        "inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs",
        tones[tone]
      )}
    >
      {Icon ? <Icon size={14} /> : null}
      {children}
    </span>
  );
};

const ModalShell = ({ open, title, subtitle, onClose, children, footer }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[70]">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="absolute inset-0 flex items-center justify-center p-4 md:p-6">
        <div className="w-full max-w-3xl rounded-2xl border border-slate-200 bg-white shadow-xl">
          <div className="p-4 md:p-5 border-b border-slate-100">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span
                    className="h-4 w-[3px] rounded-full"
                    style={{ background: ACCENT }}
                  />
                  <h3 className="text-sm md:text-base font-black tracking-tight text-slate-900">
                    {title}
                  </h3>
                </div>
                {subtitle ? (
                  <p className="mt-1 text-xs text-slate-500">{subtitle}</p>
                ) : null}
              </div>
              <button
                onClick={onClose}
                className="h-9 w-9 rounded-2xl border border-slate-200 bg-white hover:bg-slate-50 flex items-center justify-center"
              >
                <X size={16} className="text-slate-700" />
              </button>
            </div>
          </div>

          <div
            data-modal-scroll
            className="p-4 md:p-5 max-h-[70vh] overflow-auto overscroll-contain pb-10"
            style={{ scrollbarGutter: "stable" }}
          >
            {children}
          </div>

          {footer ? (
            <div className="p-4 md:p-5 border-t border-slate-100 flex items-center justify-end gap-2">
              {footer}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

const randomId = () => {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch {
    // ignore
  }
  return `id_${Math.random().toString(16).slice(2)}_${Date.now().toString(16)}`;
};

const withNodeIds = (nodes) => {
  if (!Array.isArray(nodes)) return [];
  return nodes.map((n) => {
    const out = {
      ...(n || {}),
      __id: n?.__id || randomId(),
    };
    if (Array.isArray(out.subcategories)) out.subcategories = withNodeIds(out.subcategories);
    else out.subcategories = [];
    return out;
  });
};

const stripNodeIds = (nodes) => {
  if (!Array.isArray(nodes)) return [];
  return nodes.map((n) => {
    const out = {
      name: n?.name ?? "",
      description: n?.description ?? "",
      subcategories: stripNodeIds(n?.subcategories),
    };
    return out;
  });
};

const findNodeById = (nodes, nodeId) => {
  if (!Array.isArray(nodes) || !nodeId) return null;
  for (const n of nodes) {
    if (n?.__id === nodeId) return n;
    const hit = findNodeById(n?.subcategories, nodeId);
    if (hit) return hit;
  }
  return null;
};

const SortableItem = ({ id, containerId, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    data: { containerId },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.7 : 1,
  };

  return children({ setNodeRef, style, attributes, listeners, isDragging });
};

const RearrangeTree = React.memo(function RearrangeTree({
  catId,
  nodes,
  parentId,
  depth,
  expanded,
  toggleExpanded,
}) {
  const containerId = `cat:${catId}:parent:${parentId}`;
  const ids = (Array.isArray(nodes) ? nodes : []).map((n) => n.__id);

  if (!ids.length) return null;

  return (
    <div className={cx(depth > 0 ? "pl-4 border-l border-slate-200" : "")}
    >
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <div className="grid grid-cols-1 gap-2">
          {nodes.map((node) => {
            const hasKids = Array.isArray(node?.subcategories) && node.subcategories.length > 0;
            const isOpen = expanded.has(`node:${node.__id}`);

            return (
              <SortableItem key={node.__id} id={node.__id} containerId={containerId}>
                {({ setNodeRef, style, attributes, listeners }) => (
                  <div
                    ref={setNodeRef}
                    style={style}
                    className="rounded-2xl border border-slate-200 bg-white p-3"
                  >
                    <div className="flex items-start gap-2">
                      <button
                        type="button"
                        className={cx(
                          "mt-0.5 h-7 w-7 rounded-xl border border-slate-200 bg-white flex items-center justify-center",
                          hasKids ? "text-slate-700 hover:bg-slate-50" : "text-slate-300"
                        )}
                        onClick={() => hasKids && toggleExpanded(`node:${node.__id}`)}
                        disabled={!hasKids}
                        aria-label={isOpen ? "Collapse" : "Expand"}
                      >
                        {hasKids ? (isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />) : <ChevronRight size={16} />}
                      </button>

                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-slate-900 truncate">
                          {(node?.name || "").trim() || "Untitled"}
                        </p>
                        <p className="text-xs text-slate-500 line-clamp-1">
                          {(node?.description || "").trim() || "No description"}
                        </p>
                      </div>

                      <button
                        type="button"
                        className="shrink-0 h-9 w-9 rounded-2xl border border-slate-200 bg-white hover:bg-slate-50 flex items-center justify-center cursor-grab active:cursor-grabbing"
                        title="Drag to reorder"
                        {...attributes}
                        {...listeners}
                      >
                        <GripVertical size={16} className="text-slate-500" />
                      </button>
                    </div>

                    {hasKids && isOpen && depth < CATEGORY_TREE_MAX_DEPTH - 1 ? (
                      <div className="mt-2">
                        <RearrangeTree
                          catId={catId}
                          nodes={node.subcategories}
                          parentId={node.__id}
                          depth={depth + 1}
                          expanded={expanded}
                          toggleExpanded={toggleExpanded}
                        />
                      </div>
                    ) : null}
                  </div>
                )}
              </SortableItem>
            );
          })}
        </div>
      </SortableContext>
    </div>
  );
});

/**
 * Tree helpers (3 layers total: root category + 2 nested levels)
 */
const emptyNode = () => ({ name: "", description: "", subcategories: [] });

const sanitizeTreeDepth = (node, depth = 0, maxDepth = CATEGORY_TREE_MAX_DEPTH) => {
  // depth 0 = root category, depth 1/2 = nested subcategories
  if (!node) return emptyNode();
  const out = {
    name: String(node.name ?? ""),
    description: String(node.description ?? ""),
    subcategories: Array.isArray(node.subcategories) ? node.subcategories : [],
  };
  if (depth >= maxDepth) {
    out.subcategories = [];
  } else {
    out.subcategories = out.subcategories.map((c) =>
      sanitizeTreeDepth(c, depth + 1, maxDepth)
    );
  }
  return out;
};

const validateTree = (root, maxDepth = CATEGORY_TREE_MAX_DEPTH) => {
  const errors = [];
  if (!root.name?.trim()) errors.push("Category name is required.");
  if (!root.description?.trim())
    errors.push("Category description is required.");

  const walk = (nodes, level, path) => {
    if (!Array.isArray(nodes)) return;
    nodes.forEach((n, idx) => {
      const p = `${path}${path ? " > " : ""}${
        n?.name?.trim() || `Item ${idx + 1}`
      }`;
      if (
        n &&
        (n.name?.trim() ||
          n.description?.trim() ||
          (n.subcategories?.length ?? 0) > 0)
      ) {
        // If user started creating an item, require both fields
        if (!n.name?.trim())
          errors.push(`Subcategory name is required at: ${p}`);
        if (!n.description?.trim())
          errors.push(`Subcategory description is required at: ${p}`);
      }
      if (level < maxDepth) walk(n.subcategories, level + 1, p);
    });
  };

  walk(root.subcategories, 1, root.name?.trim() || "Category");
  return errors;
};

const safeClone = (value) => {
  try {
    // structuredClone isn't supported in some browsers/environments
    if (typeof structuredClone === "function") return structuredClone(value);
  } catch {
    // fall through
  }

  // Data here is plain JSON (name/description/subcategories)
  return JSON.parse(JSON.stringify(value ?? null));
};

const keyOf = (path) =>
  Array.isArray(path) && path.length ? path.join("/") : "";

const parentKeyOf = (key) => {
  if (!key) return "";
  const parts = key.split("/");
  parts.pop();
  return parts.join("/");
};

const NodeAccordion = React.memo(function NodeAccordion({
  node,
  path,
  level,
  maxDepth,
  activeKey,
  setActiveKey,
  addChild,
  removeAt,
  setAt,
  scrollElementIntoView,
}) {
  const canAddChild = level < maxDepth;
  const thisKey = keyOf(path);
  const hasKids =
    Array.isArray(node?.subcategories) && node.subcategories.length > 0;

  const isOpen =
    activeKey === thisKey ||
    (activeKey && thisKey && activeKey.startsWith(`${thisKey}/`));

  const title = level === 1 ? "Subcategory" : "Nested Category";

  const displayName = (node?.name || "").trim() || "Untitled";
  const displayDesc = (node?.description || "").trim();

  const confirmRemove = () => {
    const ok = window.confirm(
      `Remove this item${
        displayName && displayName !== "Untitled" ? ` ("${displayName}")` : ""
      }?`
    );
    if (!ok) return;
    removeAt(path);
  };

  return (
    <div
      data-node-key={thisKey}
      className="rounded-2xl border border-slate-200 bg-white overflow-hidden"
    >
      {/* Header row */}
      <div
        role="button"
        tabIndex={0}
        onClick={() =>
          setActiveKey((prev) =>
            prev === thisKey ? parentKeyOf(thisKey) : thisKey
          )
        }
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setActiveKey((prev) =>
              prev === thisKey ? parentKeyOf(thisKey) : thisKey
            );
          }
        }}
        className={cx(
          "w-full px-4 py-3 flex items-start gap-3 text-left hover:bg-slate-50 transition-colors cursor-pointer",
          isOpen ? "bg-slate-50" : "bg-white"
        )}
      >
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setActiveKey((prev) =>
              prev === thisKey ? parentKeyOf(thisKey) : thisKey
            );
          }}
          className={cx(
            "mt-0.5 h-7 w-7 rounded-xl border border-slate-200 bg-white flex items-center justify-center",
            hasKids ? "text-slate-700 hover:bg-slate-50" : "text-slate-400"
          )}
          aria-label={isOpen ? "Collapse" : "Expand"}
          disabled={!hasKids}
        >
          {hasKids ? (
            isOpen ? (
              <ChevronDown size={16} />
            ) : (
              <ChevronRight size={16} />
            )
          ) : (
            <ChevronRight size={16} />
          )}
        </button>

        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[11px] font-black uppercase tracking-[0.18em] text-slate-400">
                {title} • Level {level}
              </p>
              <p className="mt-1 text-sm font-semibold text-slate-900 truncate">
                {displayName}
              </p>
              {displayDesc ? (
                <p className="mt-0.5 text-xs text-slate-500 line-clamp-1">
                  {displayDesc}
                </p>
              ) : (
                <p className="mt-0.5 text-xs text-slate-400">No description</p>
              )}
            </div>

            <span className="shrink-0 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs text-slate-600">
              Children:{" "}
              <span className="font-semibold text-slate-900">
                {hasKids ? node.subcategories.length : 0}
              </span>
            </span>
          </div>

          <div className="mt-2 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setActiveKey(thisKey);
              }}
              className="inline-flex items-center gap-2 rounded-2xl px-3 py-2 text-xs font-semibold border border-slate-200 bg-white text-slate-900 hover:bg-slate-50 transition-colors"
            >
              <Pencil size={14} />
              Edit
            </button>

            {canAddChild ? (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  addChild(path);
                }}
                className="inline-flex items-center gap-2 rounded-2xl px-3 py-2 text-xs font-semibold border border-slate-200 bg-white text-slate-900 hover:bg-slate-50 transition-colors"
              >
                <Plus size={14} />
                Add Child
              </button>
            ) : (
              <span className="text-xs text-slate-500">Max depth reached</span>
            )}

            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                confirmRemove();
              }}
              className="inline-flex items-center gap-2 rounded-2xl px-3 py-2 text-xs font-semibold border border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100 transition-colors"
            >
              <Trash2 size={14} />
              Remove
            </button>
          </div>
        </div>
      </div>

      {/* Details */}
      {isOpen ? (
        <div className="px-4 pb-4">
          <div className="pt-2 flex items-center justify-between gap-3">
            <p className="text-xs text-slate-500">
              Edit name/description here. Nested depth is limited to two subcategory levels.
            </p>
            <span className="text-xs text-slate-500">
              Tip: use header buttons for quick actions
            </span>
          </div>

          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-black uppercase tracking-[0.18em] text-slate-400">
                Name
              </label>
              <input
                value={node.name || ""}
                onChange={(e) => setAt(path, "name", e.target.value)}
                onPaste={(e) => {
                  // Pasting can push the editor just below the fold; keep it visible.
                  window.setTimeout(
                    () => scrollElementIntoView(e.currentTarget, "auto"),
                    0
                  );
                }}
                onFocus={() => {
                  setActiveKey(thisKey);
                  window.setTimeout(() => {
                    scrollElementIntoView(document.activeElement, "auto");
                  }, 0);
                }}
                data-editor="name"
                className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
                placeholder="e.g., Electronics"
              />
            </div>

            <div>
              <label className="block text-[11px] font-black uppercase tracking-[0.18em] text-slate-400">
                Description
              </label>
              <input
                value={node.description || ""}
                onChange={(e) => setAt(path, "description", e.target.value)}
                onPaste={(e) => {
                  window.setTimeout(
                    () => scrollElementIntoView(e.currentTarget, "auto"),
                    0
                  );
                }}
                onFocus={() => {
                  setActiveKey(thisKey);
                  window.setTimeout(() => {
                    scrollElementIntoView(document.activeElement, "auto");
                  }, 0);
                }}
                data-editor="description"
                className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
                placeholder="Short description"
              />
            </div>
          </div>

          <div className="mt-4">
            <div className="flex items-center justify-between">
              <p className="text-[11px] font-black uppercase tracking-[0.18em] text-slate-400">
                Children
              </p>
              <span className="text-xs text-slate-500">
                {canAddChild ? "Use Add Child above" : "Max depth reached"}
              </span>
            </div>

            {hasKids ? (
              <div className="mt-3 grid grid-cols-1 gap-2">
                {node.subcategories.map((child, idx) => (
                  <div key={idx} className="pl-3 border-l border-slate-200">
                    <NodeAccordion
                      node={child}
                      path={[...path, idx]}
                      level={level + 1}
                      maxDepth={maxDepth}
                      activeKey={activeKey}
                      setActiveKey={setActiveKey}
                      addChild={addChild}
                      removeAt={removeAt}
                      setAt={setAt}
                      scrollElementIntoView={scrollElementIntoView}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="mt-3 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-xs text-slate-600">
                No children yet.
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
});

const TreeEditor = ({ value, onChange, maxDepth = CATEGORY_TREE_MAX_DEPTH }) => {
  const update = (next) => onChange(sanitizeTreeDepth(next, 0, maxDepth));

  const containerRef = useRef(null);

  const [activeKey, setActiveKey] = useState("");

  const getModalScrollRoot = useCallback(() => {
    const root = containerRef.current;
    if (!root) return null;
    return root.closest?.("[data-modal-scroll]") || null;
  }, []);

  const scrollElementIntoView = useCallback((el, behavior = "auto") => {
    if (!el) return;
    const scrollRoot = getModalScrollRoot();
    if (!scrollRoot) {
      el.scrollIntoView({ block: "nearest", inline: "nearest", behavior });
      return;
    }

    const pad = 16;
    const elRect = el.getBoundingClientRect();
    const rootRect = scrollRoot.getBoundingClientRect();

    // If element is above the visible area
    if (elRect.top < rootRect.top + pad) {
      scrollRoot.scrollBy({
        top: elRect.top - rootRect.top - pad,
        behavior,
      });
      return;
    }

    // If element is below the visible area
    if (elRect.bottom > rootRect.bottom - pad) {
      scrollRoot.scrollBy({
        top: elRect.bottom - rootRect.bottom + pad,
        behavior,
      });
    }
  }, [getModalScrollRoot]);

  const scrollToEditorForKey = useCallback((key, behavior = "auto") => {
    if (!key) return;
    const root = containerRef.current;
    if (!root) return;

    const safeKey =
      typeof CSS !== "undefined" && typeof CSS.escape === "function"
        ? CSS.escape(key)
        : key.replace(/"/g, '\\"');
    const nodeEl = root.querySelector(`[data-node-key="${safeKey}"]`);
    if (!nodeEl) return;

    const inputEl = nodeEl.querySelector('input[data-editor="name"]');
    scrollElementIntoView(inputEl || nodeEl, behavior);
  }, [scrollElementIntoView]);

  useEffect(() => {
    if (!activeKey) return;
    // Defer so the DOM (expanded section) exists before scrolling.
    const id = window.setTimeout(
      () => scrollToEditorForKey(activeKey, "smooth"),
      0
    );
    return () => window.clearTimeout(id);
  }, [activeKey, scrollToEditorForKey]);

  const setRootField = (key, v) => update({ ...value, [key]: v });

  const addChild = (path) => {
    // path: array of indices to reach parent in root.subcategories
    const next = safeClone(value);
    let parent = next;
    for (let i = 0; i < path.length; i++) {
      parent = parent?.subcategories?.[path[i]];
    }
    if (!parent) return;
    parent.subcategories = parent.subcategories || [];
    parent.subcategories.push(emptyNode());
    update(next);

    // Focus the newly added node
    const newIdx = parent.subcategories.length - 1;
    const newKey = keyOf([...path, newIdx]);
    setActiveKey(newKey);
  };

  const removeAt = (path) => {
    const next = safeClone(value);
    const idx = path[path.length - 1];
    const parentPath = path.slice(0, -1);
    let parent = next;
    for (let i = 0; i < parentPath.length; i++) {
      parent = parent?.subcategories?.[parentPath[i]];
    }
    if (!parent?.subcategories) return;
    parent.subcategories.splice(idx, 1);
    update(next);

    const removedKey = keyOf(path);
    setActiveKey((prev) => {
      if (!prev) return "";
      if (prev === removedKey) return keyOf(parentPath);
      if (prev.startsWith(`${removedKey}/`)) return keyOf(parentPath);
      return prev;
    });
  };

  const setAt = (path, key, v) => {
    const next = safeClone(value);
    let node = next;
    for (let i = 0; i < path.length; i++) node = node?.subcategories?.[path[i]];
    if (!node) return;
    node[key] = v;
    update(next);
  };

  return (
    <div ref={containerRef}>
      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 md:p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[11px] font-black uppercase tracking-[0.18em] text-slate-400">
              Category Core
            </p>
            <p className="mt-1 text-xs text-slate-500">
              Root name/description are required. Nested items are optional.
            </p>
          </div>
          <div className="h-10 w-10 rounded-2xl border border-slate-200 bg-white flex items-center justify-center">
            <FolderTree size={18} className="text-slate-700" />
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-[11px] font-black uppercase tracking-[0.18em] text-slate-400">
              Name
            </label>
            <input
              value={value.name || ""}
              onChange={(e) => setRootField("name", e.target.value)}
              className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
              placeholder="e.g., Fashion"
            />
          </div>

          <div>
            <label className="block text-[11px] font-black uppercase tracking-[0.18em] text-slate-400">
              Description
            </label>
            <input
              value={value.description || ""}
              onChange={(e) => setRootField("description", e.target.value)}
              className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
              placeholder="Short description"
            />
          </div>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between">
        <p className="text-[11px] font-black uppercase tracking-[0.18em] text-slate-400">
          Subcategories
        </p>
        <button
          type="button"
          onClick={() => addChild([])} // add to root.subcategories
          className="inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold text-white bg-slate-900 hover:bg-[#D2042D] transition-colors"
        >
          <Plus size={16} />
          Add Subcategory
        </button>
      </div>

      {Array.isArray(value.subcategories) && value.subcategories.length > 0 ? (
        <div className="mt-3 grid grid-cols-1 gap-2">
          {value.subcategories.map((node, idx) => (
            <NodeAccordion
              key={idx}
              node={node}
              path={[idx]}
              level={1}
              maxDepth={maxDepth}
              activeKey={activeKey}
              setActiveKey={setActiveKey}
              addChild={addChild}
              removeAt={removeAt}
              setAt={setAt}
              scrollElementIntoView={scrollElementIntoView}
            />
          ))}
        </div>
      ) : (
        <div className="mt-3 rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-600">
          No subcategories added yet.
        </div>
      )}
    </div>
  );
};

const TreePreview = ({ node, expanded, toggle, path = "" }) => {
  const hasKids =
    Array.isArray(node?.subcategories) && node.subcategories.length > 0;
  const isOpen = expanded.has(path);

  return (
    <div className="py-1">
      <div className="flex items-start gap-2">
        <button
          type="button"
          onClick={() => hasKids && toggle(path)}
          className={cx(
            "mt-0.5 h-6 w-6 rounded-xl border border-slate-200 bg-white flex items-center justify-center",
            hasKids ? "hover:bg-slate-50" : "opacity-50 cursor-default"
          )}
        >
          {hasKids ? (
            isOpen ? (
              <ChevronDown size={14} />
            ) : (
              <ChevronRight size={14} />
            )
          ) : (
            <span />
          )}
        </button>

        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-slate-900 truncate">
            {node?.name || "—"}
          </p>
          <p className="text-xs text-slate-500 line-clamp-2">
            {node?.description || ""}
          </p>

          {hasKids && isOpen ? (
            <div className="mt-2 pl-4 border-l border-slate-200">
              {node.subcategories.map((c, idx) => (
                <TreePreview
                  key={`${path}/${idx}`}
                  node={c}
                  expanded={expanded}
                  toggle={toggle}
                  path={`${path}/${idx}`}
                />
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default function CategoryManagement() {
  // Backend is mounted at: http://localhost:8080/api/category
  // Use the shared axios instance (util/API.js) so baseURL + auth headers apply.
  const BASE = "/category";

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);

  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [mode, setMode] = useState("create"); // create | edit
  const [activeId, setActiveId] = useState(null);
  const [form, setForm] = useState({
    name: "",
    description: "",
    subcategories: [],
  });

  const [expanded, setExpanded] = useState(() => new Set());

  const [rearrangeOpen, setRearrangeOpen] = useState(false);
  const [rearrangeSaving, setRearrangeSaving] = useState(false);
  const [rearrangeDraft, setRearrangeDraft] = useState([]); // [{ _id, name, description, subcategories(with __id) }]
  const [rearrangeExpanded, setRearrangeExpanded] = useState(() => new Set());

  // Prevent background scroll-jank while typing/pasting in the modal.
  useEffect(() => {
    if (!modalOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [modalOpen]);

  const toggleExpand = (path) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  };

  const fetchAll = async () => {
    try {
      setError("");
      setLoading(true);
      const res = await API.get(BASE); // public in your backend; if you lock it later, token will still work through API instance
      setItems(Array.isArray(res.data) ? res.data : []);
      setLastUpdated(new Date());
    } catch (e) {
      setError("Failed to load categories. Please refresh.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return items;

    const matchNode = (node) => {
      const s = `${node?.name ?? ""} ${node?.description ?? ""}`.toLowerCase();
      if (s.includes(needle)) return true;
      if (Array.isArray(node?.subcategories))
        return node.subcategories.some(matchNode);
      return false;
    };

    return items.filter(matchNode);
  }, [items, q]);

  const openCreate = () => {
    setMode("create");
    setActiveId(null);
    setForm({ name: "", description: "", subcategories: [] });
    setModalOpen(true);
  };

  const openEdit = (cat) => {
    setMode("edit");
    setActiveId(cat?._id);
    setForm(
      sanitizeTreeDepth(
        {
          name: cat?.name ?? "",
          description: cat?.description ?? "",
          subcategories: cat?.subcategories ?? [],
        },
        0,
        CATEGORY_TREE_MAX_DEPTH
      )
    );
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
  };

  const save = async () => {
    const payload = sanitizeTreeDepth(form, 0, CATEGORY_TREE_MAX_DEPTH);
    const errs = validateTree(payload, CATEGORY_TREE_MAX_DEPTH);
    if (errs.length) {
      toast?.error ? toast.error(errs[0]) : alert(errs[0]);
      return;
    }

    try {
      setSaving(true);
      if (mode === "create") {
        await API.post(BASE, payload);
        toast?.success?.("Category created");
      } else {
        await API.put(`${BASE}/${activeId}`, payload);
        toast?.success?.("Category updated");
      }
      setModalOpen(false);
      await fetchAll();
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        "Failed to save category. Please try again.";
      toast?.error ? toast.error(msg) : alert(msg);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (cat) => {
    const ok = window.confirm(
      `Delete category "${cat?.name}"? This cannot be undone.`
    );
    if (!ok) return;

    try {
      await API.delete(`${BASE}/${cat?._id}`);
      toast?.success?.("Category deleted");
      await fetchAll();
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        "Failed to delete category.";
      toast?.error ? toast.error(msg) : alert(msg);
    }
  };

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const toggleRearrangeExpanded = (key) => {
    setRearrangeExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const openRearrange = () => {
    const sorted = [...items].sort((a, b) => {
      const ap = Number.isFinite(a?.position) ? a.position : 0;
      const bp = Number.isFinite(b?.position) ? b.position : 0;
      if (ap !== bp) return ap - bp;
      return String(b?.createdAt || "").localeCompare(String(a?.createdAt || ""));
    });
    setRearrangeDraft(
      sorted.map((c) => ({
        ...c,
        subcategories: withNodeIds(c?.subcategories),
      }))
    );
    setRearrangeExpanded(new Set());
    setRearrangeOpen(true);
  };

  const closeRearrange = () => {
    if (rearrangeSaving) return;
    setRearrangeOpen(false);
  };

  const reorderInCategory = (draftCats, catId, parentId, activeId, overId) => {
    const next = safeClone(draftCats);
    const cat = next.find((c) => c?._id === catId);
    if (!cat) return draftCats;

    const getList = () => {
      if (parentId === "root") return cat.subcategories;
      const parentNode = findNodeById(cat.subcategories, parentId);
      return parentNode?.subcategories;
    };

    const list = getList();
    if (!Array.isArray(list)) return draftCats;
    const oldIndex = list.findIndex((n) => n?.__id === activeId);
    const newIndex = list.findIndex((n) => n?.__id === overId);
    if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) return draftCats;
    const moved = arrayMove(list, oldIndex, newIndex);

    if (parentId === "root") {
      cat.subcategories = moved;
      return next;
    }

    const parentNode = findNodeById(cat.subcategories, parentId);
    if (!parentNode) return draftCats;
    parentNode.subcategories = moved;
    return next;
  };

  const onRearrangeDragEnd = (event) => {
    const { active, over } = event;
    if (!active?.id || !over?.id) return;

    const activeContainer = active.data?.current?.containerId;
    const overContainer = over.data?.current?.containerId;
    if (!activeContainer || activeContainer !== overContainer) return;

    if (activeContainer === "categories") {
      setRearrangeDraft((prev) => {
        const oldIndex = prev.findIndex((c) => `cat:${c?._id}` === active.id);
        const newIndex = prev.findIndex((c) => `cat:${c?._id}` === over.id);
        if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) return prev;
        return arrayMove(prev, oldIndex, newIndex);
      });
      return;
    }

    // containerId format: cat:<catId>:parent:<parentId>
    const m = /^cat:([^:]+):parent:(.+)$/.exec(activeContainer);
    if (!m) return;
    const catId = m[1];
    const parentId = m[2];
    setRearrangeDraft((prev) => reorderInCategory(prev, catId, parentId, active.id, over.id));
  };

  const saveRearrange = async () => {
    try {
      setRearrangeSaving(true);
      const payloadItems = rearrangeDraft.map((c, idx) => ({
        id: c._id,
        position: idx,
        subcategories: stripNodeIds(c.subcategories),
      }));
      await API.put(`${BASE}/reorder`, { items: payloadItems });
      toast?.success?.("Category order updated");
      setRearrangeOpen(false);
      await fetchAll();
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        "Failed to save order. Please try again.";
      toast?.error ? toast.error(msg) : alert(msg);
    } finally {
      setRearrangeSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-[1440px] mx-auto px-4 md:px-6 py-6 md:py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6 md:mb-8">
          <div className="min-w-0">
            <h1 className="text-2xl md:text-3xl font-black tracking-tight text-slate-900">
              Category Management
            </h1>
            <p className="mt-1 text-sm text-slate-600">
              Create, edit and organize categories with up to three layers.
            </p>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Pill icon={Clock} tone="neutral">
                {lastUpdated ? (
                  <span>
                    Updated{" "}
                    <span className="font-semibold text-slate-900">
                      {lastUpdated.toLocaleTimeString()}
                    </span>
                  </span>
                ) : (
                  <span>Not updated yet</span>
                )}
              </Pill>

              {error ? (
                <Pill icon={AlertTriangle} tone="bad">
                  {error}
                </Pill>
              ) : (
                <Pill icon={CheckCircle2} tone="good">
                  System normal
                </Pill>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={fetchAll}
              disabled={loading}
              className={cx(
                "inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold border transition-colors",
                loading
                  ? "border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed"
                  : "border-slate-200 bg-white text-slate-900 hover:bg-slate-50"
              )}
            >
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>

            <button
              onClick={openRearrange}
              disabled={loading || items.length === 0}
              className={cx(
                "inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold border transition-colors",
                loading || items.length === 0
                  ? "border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed"
                  : "border-slate-200 bg-white text-slate-900 hover:bg-slate-50"
              )}
              title="Reorder categories and subcategories"
            >
              <GripVertical size={16} className="text-slate-700" />
              Re-Arrange
            </button>

            <button
              onClick={openCreate}
              className="inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold text-white bg-slate-900 hover:bg-[#D2042D] transition-colors"
            >
              <Plus size={16} />
              Add Category
            </button>
          </div>
        </div>

        <ModalShell
          open={rearrangeOpen}
          title="Re-Arrange Categories"
          subtitle="Drag items to change order. Expand a category to reorder subcategories."
          onClose={closeRearrange}
          footer={
            <>
              <button
                onClick={closeRearrange}
                disabled={rearrangeSaving}
                className={cx(
                  "inline-flex items-center justify-center rounded-2xl px-4 py-2 text-sm font-semibold border",
                  rearrangeSaving
                    ? "border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed"
                    : "border-slate-200 bg-white text-slate-900 hover:bg-slate-50"
                )}
              >
                Cancel
              </button>
              <button
                onClick={saveRearrange}
                disabled={rearrangeSaving}
                className={cx(
                  "inline-flex items-center justify-center rounded-2xl px-4 py-2 text-sm font-semibold text-white bg-slate-900 hover:bg-[#D2042D] transition-colors",
                  rearrangeSaving ? "opacity-60 cursor-not-allowed" : ""
                )}
              >
                {rearrangeSaving ? "Saving…" : "Save Order"}
              </button>
            </>
          }
        >
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={onRearrangeDragEnd}
          >
            <SortableContext
              items={rearrangeDraft.map((c) => `cat:${c?._id}`)}
              strategy={verticalListSortingStrategy}
            >
              <div className="grid grid-cols-1 gap-3">
                {rearrangeDraft.map((cat) => {
                  const catKey = `cat:${cat?._id}`;
                  const isOpen = rearrangeExpanded.has(catKey);
                  const hasKids = Array.isArray(cat?.subcategories) && cat.subcategories.length > 0;

                  return (
                    <SortableItem
                      key={catKey}
                      id={catKey}
                      containerId="categories"
                    >
                      {({ setNodeRef, style, attributes, listeners }) => (
                        <div
                          ref={setNodeRef}
                          style={style}
                          className="rounded-2xl border border-slate-200 bg-white p-4"
                        >
                          <div className="flex items-start gap-2">
                            <button
                              type="button"
                              className={cx(
                                "mt-0.5 h-7 w-7 rounded-xl border border-slate-200 bg-white flex items-center justify-center",
                                hasKids ? "text-slate-700 hover:bg-slate-50" : "text-slate-300"
                              )}
                              onClick={() => hasKids && toggleRearrangeExpanded(catKey)}
                              disabled={!hasKids}
                              aria-label={isOpen ? "Collapse" : "Expand"}
                            >
                              {hasKids ? (isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />) : <ChevronRight size={16} />}
                            </button>

                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-black tracking-tight text-slate-900 truncate">
                                {cat?.name}
                              </p>
                              <p className="mt-0.5 text-xs text-slate-500 line-clamp-1">
                                {cat?.description}
                              </p>
                            </div>

                            <button
                              type="button"
                              className="shrink-0 h-9 w-9 rounded-2xl border border-slate-200 bg-white hover:bg-slate-50 flex items-center justify-center cursor-grab active:cursor-grabbing"
                              title="Drag to reorder"
                              {...attributes}
                              {...listeners}
                            >
                              <GripVertical size={16} className="text-slate-500" />
                            </button>
                          </div>

                          {hasKids && isOpen ? (
                            <div className="mt-3">
                              <p className="text-[11px] font-black uppercase tracking-[0.18em] text-slate-400 mb-2">
                                Subcategories
                              </p>
                              <RearrangeTree
                                catId={cat?._id}
                                nodes={cat.subcategories}
                                parentId="root"
                                depth={0}
                                expanded={rearrangeExpanded}
                                toggleExpanded={toggleRearrangeExpanded}
                              />
                            </div>
                          ) : null}
                        </div>
                      )}
                    </SortableItem>
                  );
                })}
              </div>
            </SortableContext>
          </DndContext>
        </ModalShell>

        {/* Search + summary */}
        <Card className="p-4 md:p-5 mb-4 md:mb-5">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[11px] font-black uppercase tracking-[0.18em] text-slate-400">
                Search
              </p>
              <div className="mt-2 relative">
                <Search
                  size={16}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  className="w-full md:w-[520px] rounded-2xl border border-slate-200 bg-white pl-11 pr-4 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
                  placeholder="Search by name/description (includes subcategories)"
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs text-slate-600">
                <span className="h-2 w-2 rounded-full bg-slate-400" />
                Total:{" "}
                <span className="font-semibold text-slate-900">
                  {items.length}
                </span>
              </span>
              <span
                className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold"
                style={{
                  borderColor: `${ACCENT}22`,
                  background: `${ACCENT}10`,
                  color: ACCENT,
                }}
              >
                Filtered: {filtered.length}
              </span>
            </div>
          </div>
        </Card>

        {/* List */}
        <Card className="p-4 md:p-5">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span
                  className="h-4 w-[3px] rounded-full"
                  style={{ background: ACCENT }}
                />
                <h2 className="text-sm md:text-base font-black tracking-tight text-slate-900">
                  Categories
                </h2>
              </div>
              <p className="mt-1 text-xs text-slate-500">
                Expand any item to preview nested structure.
              </p>
            </div>
          </div>

          <div className="mt-4">
            {loading ? (
              <div className="grid grid-cols-1 gap-3">
                <Skeleton className="h-[96px]" />
                <Skeleton className="h-[96px]" />
                <Skeleton className="h-[96px]" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-center">
                <p className="text-sm font-semibold text-slate-900">
                  No categories found
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  Try changing your search or create a new category.
                </p>
                <button
                  onClick={openCreate}
                  className="mt-4 inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold text-white bg-slate-900 hover:bg-[#D2042D] transition-colors"
                >
                  <Plus size={16} />
                  Add Category
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-3">
                {filtered.map((cat) => (
                  <div
                    key={cat._id}
                    className="rounded-2xl border border-slate-200 bg-white p-4 md:p-5"
                  >
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-[11px] font-black uppercase tracking-[0.18em] text-slate-400">
                          Category
                        </p>
                        <p className="mt-1 text-lg font-black tracking-tight text-slate-900">
                          {cat.name}
                        </p>
                        <p className="mt-1 text-sm text-slate-600 line-clamp-2">
                          {cat.description}
                        </p>

                        <div className="mt-3 flex flex-wrap gap-2">
                          <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs text-slate-600">
                            <span className="h-2 w-2 rounded-full bg-slate-400" />
                            Subcategories:{" "}
                            <span className="font-semibold text-slate-900">
                              {Array.isArray(cat.subcategories)
                                ? cat.subcategories.length
                                : 0}
                            </span>
                          </span>
                          <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs text-slate-600">
                            ID:{" "}
                            <span className="font-mono text-[11px] text-slate-700">
                              {cat._id}
                            </span>
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openEdit(cat)}
                          className="inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold border border-slate-200 bg-white text-slate-900 hover:bg-slate-50 transition-colors"
                        >
                          <Pencil size={16} />
                          Edit
                        </button>

                        <button
                          onClick={() => remove(cat)}
                          className="inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold border border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100 transition-colors"
                        >
                          <Trash2 size={16} />
                          Delete
                        </button>
                      </div>
                    </div>

                    <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <p className="text-[11px] font-black uppercase tracking-[0.18em] text-slate-400">
                        Structure Preview
                      </p>

                      <div className="mt-3">
                        <TreePreview
                          node={{
                            name: cat.name,
                            description: cat.description,
                            subcategories: cat.subcategories || [],
                          }}
                          expanded={expanded}
                          toggle={toggleExpand}
                          path={cat._id}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>

        {/* Modal */}
        <ModalShell
          open={modalOpen}
          onClose={closeModal}
          title={mode === "create" ? "Add Category" : "Edit Category"}
          subtitle="Maintain a clean taxonomy. Keep names short and descriptions explicit."
          footer={
            <>
              <button
                onClick={closeModal}
                disabled={saving}
                className={cx(
                  "inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold border transition-colors",
                  saving
                    ? "border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed"
                    : "border-slate-200 bg-white text-slate-900 hover:bg-slate-50"
                )}
              >
                Cancel
              </button>
              <button
                onClick={save}
                disabled={saving}
                className={cx(
                  "inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold text-white transition-colors",
                  saving
                    ? "bg-slate-400 cursor-not-allowed"
                    : "bg-slate-900 hover:bg-[#D2042D]"
                )}
              >
                {saving ? (
                  <RefreshCw size={16} className="animate-spin" />
                ) : null}
                {mode === "create" ? "Create" : "Update"}
              </button>
            </>
          }
        >
          <TreeEditor value={form} onChange={setForm} maxDepth={CATEGORY_TREE_MAX_DEPTH} />

          <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <p className="text-xs text-slate-600">
              Tip: You can keep subcategories empty now and expand later. The UI enforces 3 layers.
            </p>
          </div>
        </ModalShell>
      </div>
    </div>
  );
}
